import React, { useEffect, useState } from "react";
import Header from "components/Header";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";

// ================== COMPACT CHART OPTIONS ==================
const compactChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10 } } },
        datalabels: false
    },
    scales: {
        x: {
            ticks: { font: { size: 10 } },
            grid: { display: false },
        },
        y: {
            ticks: { font: { size: 10 } },
            grid: { color: "#E5E7EB" },
        },
    },
};
const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%", // donut thickness
    plugins: {
        legend: {
            position: "bottom",
            labels: {
                boxWidth: 10,
                font: { size: 10 },
            },
        },
        datalabels: false
    },
   
};

// ================== KPI CARD ==================
const KpiCard = ({ title, value, color }) => (
    <div className="bg-white p-3 rounded-xl shadow-sm border-l-4" style={{ borderColor: color }}>
        <p className="text-xs text-gray-500">{title}</p>
        <p className="text-lg font-semibold text-gray-800 mt-1">{value}</p>
    </div>
);

// ================== DATA ==================
const screenedKPIs = [
    // { label: "6–59 Months", value: "72%", color: "#F59E0B" },
    // { label: "5–9 Years", value: "68%", color: "#FBBF24" },
    // { label: "10–19 Years", value: "75%", color: "#3B82F6" },
    { label: "Women (15–49)", value: "70%", color: "#EC4899" },
    { label: "Pregnant Women", value: "82%", color: "#10B981" },
];

const facilityKPIs = [
    { label: "PHCs Screening", value: "92%", color: "#10B981" },
    { label: "CHCs Screening", value: "88%", color: "#3B82F6" },
    { label: "District Hospitals", value: "100%", color: "#6366F1" },
];

const ageWiseCoverage = {
    labels: ["6–59 Months", "5–9 Years", "10–19 Years (Adolescents)", "15-49 years (Women of Reproductive Age)", "Over 65 (Older Adults)"],
    datasets: [
        {
            label: "Screened (%)",
            data: [72, 68, 75, 43, 12],
            backgroundColor: ["#F59E0B", "#FBBF24", "#3B82F6", "#EC4899", "#10B981"],
            borderRadius: 6,
        },
    ],
};

const districtCoverage = {
    labels: ["High", "Medium", "Low"],
    datasets: [
        {
            data: [45, 35, 20],
            backgroundColor: ["#10B981", "#F59E0B", "#EF4444"],
            borderWidth: 0,
        },
    ],
};
const topDistrictsData = {
    labels: [
        "Nandurbar",
        "Gadchiroli",
        "Hingoli",
        "Washim",
        "Buldhana",
        "Osmanabad",
        "Parbhani",
        "Jalna",
        "Beed",
        "Yavatmal",
    ],
    datasets: [
        {
            label: "Anemia Prevalence (%)",
            data: [58, 56, 54, 53, 52, 51, 50, 49, 48, 47],
            backgroundColor: [
                "#7F1D1D",
                "#991B1B",
                "#B91C1C",
                "#DC2626",
                "#EF4444",
                "#F97316",
                "#F59E0B",
                "#FACC15",
                "#84CC16",
                "#16A34A",
            ],
            borderRadius: 6,
            barThickness: 10,
        },
    ],
};

const topDistrictsOptions = {
    indexAxis: "y", // 👈 horizontal bars
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: (ctx) => `${ctx.raw}% anemia prevalence`,
            },
        },
        datalabels: false
    },
    scales: {
        x: {
            max: 60,
            ticks: { font: { size: 10 } },
            grid: { color: "#E5E7EB" },
        },
        y: {
            ticks: { font: { size: 10 } },
            grid: { display: false },
        },
    },
};

// ================== DASHBOARD ==================
export default function AnemiaDashboard() {
    const [stateGeoJSON, setStateGeoJSON] = useState(null);

    // Dummy anemia prevalence per district (replace with API)
    const districtAnemia = {
        Nandurbar: 58,
        Gadchiroli: 56,
        Hingoli: 54,
        Washim: 53,
        Buldhana: 52,
        Osmanabad: 51,
        Parbhani: 50,
        Jalna: 49,
        Beed: 48,
        Yavatmal: 47,
    };

    // Color scale based on anemia prevalence
    const getColor = (value) => {
        if (value >= 55) return "#7F1D1D"; // very high
        if (value >= 50) return "#DC2626"; // high
        if (value >= 45) return "#F97316"; // moderate
        if (value >= 40) return "#FACC15"; // mild
        return "#16A34A"; // low
    };

    // Load Maharashtra GeoJSON
    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/geojson/1.geojson")
            .then((res) => res.json())
            .then((data) => setStateGeoJSON(data))
            .catch((err) => console.error("State GeoJSON load error:", err));
    }, []);
    const handleMenuClick = () => console.log("menu clicked (header)");
    return (
        <div className="min-h-screen bg-gray-50">
            <Header onMenuClick={handleMenuClick} dashboardName="Anemia Dashboard" />
            <div className="px-3 pt-1 pb-3 space-y-4 bg-gray-50">

                {/* SECTION: COVERAGE & SCREENING */}
                <section className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                        Coverage & Screening 
                    </h2>

                    {/* KPI ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                        {/* 1️⃣ AGE-WISE SCREENING */}
                        <div className="bg-white rounded-xl shadow-sm p-3 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                Age-wise Screening Coverage (%)
                            </h3>

                            <div className="flex-1">
                                <div className="relative w-full h-100">
                                    <Bar data={ageWiseCoverage} options={compactChartOptions} />
                                </div>
                            </div>
                        </div>

                        {/* 2️⃣ DISTRICT COVERAGE DONUT */}
                        <div className="bg-white rounded-xl shadow-sm p-3 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                District-wise Screening Coverage
                            </h3>

                            <div className="flex-1 flex items-center justify-center">
                                <div className="relative w-full h-100">
                                    <Doughnut data={districtCoverage} options={donutOptions} />
                                </div>
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-1">
                                45% districts ≥80% coverage
                            </p>
                        </div>

                        {/* 3️⃣ FACILITY COVERAGE KPIs */}
                        <div className=" flex flex-col justify-between">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                Screening Facilities
                            </h3>

                            <div className="grid grid-cols-1 gap-2">
                                {facilityKPIs.map((item) => (
                                    <KpiCard
                                        key={item.label}
                                        title={item.label}
                                        value={item.value}
                                        color={item.color}
                                    />
                                ))}
                                {screenedKPIs.map((item) => (
                                    <KpiCard
                                        key={item.label}
                                        title={item.label}
                                        value={item.value}
                                        color={item.color}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section >

                {/* ================= SECTION: ANEMIA PREVALENCE & BURDEN ================= */}
                < section className="space-y-4" >
                    <h2 className="text-lg font-semibold text-gray-800">
                        Anemia Prevalence & Burden 
                    </h2>

                    {/* KPI SUMMARY */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard title="Overall Anemia Prevalence" value="44%" color="#EF4444" />
                        <KpiCard title="Moderate + Severe" value="32%" color="#F97316" />
                        <KpiCard title="Severe Anemia (Hb ≤7)" value="6%" color="#B91C1C" />
                        <KpiCard title="Children <5 Anemic" value="41%" color="#F59E0B" />
                    </div>

                    {/* VISUAL ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">

                        {/* STACKED BAR */}
                        <div className="bg-white rounded-xl shadow-sm p-3 lg:col-span-2 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                Severity Distribution – Pregnant Women
                            </h3>

                            <div className="flex-1">
                                <div className="relative w-full h-[260px]">
                                    <Bar
                                        data={{
                                            labels: ["Pregnant Women"],
                                            datasets: [
                                                { label: "Severe (≤7)", data: [6], backgroundColor: "#B91C1C" },
                                                { label: "Moderate (7.1–10.9)", data: [38], backgroundColor: "#F97316" },
                                                { label: "Normal (≥11)", data: [56], backgroundColor: "#16A34A" },
                                            ],
                                        }}
                                        options={{
                                            ...compactChartOptions,
                                            scales: {
                                                x: { stacked: true },
                                                y: { stacked: true, max: 100 },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* DONUT */}
                        <div className="bg-white rounded-xl shadow-sm p-3 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                Pregnant Women – Hb Status
                            </h3>

                            <div className="flex-1 flex items-center justify-center">
                                <div className="relative w-full h-[260px]">
                                    <Doughnut
                                        data={{
                                            labels: ["Severe", "Moderate", "Normal"],
                                            datasets: [
                                                {
                                                    data: [6, 38, 56],
                                                    backgroundColor: ["#B91C1C", "#F97316", "#16A34A"],
                                                },
                                            ],
                                        }}
                                        options={donutOptions}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>


                    {/* TREATMENT & OUTCOME KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard title="Receiving Iron Sucrose" value="61%" color="#2563EB" />
                        <KpiCard title="Hb Improvement Rate" value="58%" color="#0EA5E9" />
                        <KpiCard title="Severe Anemia Hotspot Districts" value="14" color="#DC2626" />
                        <KpiCard title="Micronutrient Coverage" value="73%" color="#16A34A" />
                    </div>
                </section >

                {/* ================= SECTION: IFA & SUPPLEMENTATION COVERAGE ================= */}
                < section className="space-y-4" >
                    <h2 className="text-lg font-semibold text-gray-800">
                        IFA & Supplementation Coverage 
                    </h2>

                    {/* KPI SUMMARY */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard title="IFA Coverage – Pregnant Women" value="76%" color="#2563EB" />
                        <KpiCard title="IFA Coverage – Lactating Mothers" value="69%" color="#7C3AED" />
                        <KpiCard title="WIFS Coverage – Adolescents" value="63%" color="#0EA5E9" />
                        <KpiCard title="IFA Compliance Rate" value="58%" color="#16A34A" />
                    </div>

                    {/* VISUAL ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                        {/* IFA COVERAGE BAR */}
                        <div className="bg-white rounded-xl shadow-sm p-3 lg:col-span-2">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                IFA / WIFS Coverage by Beneficiary Group (%)
                            </h3>

                            {/* ✅ FIXED HEIGHT */}
                            <div className="relative w-full h-[260px]">
                                <Bar
                                    data={{
                                        labels: ["Pregnant Women", "Lactating Mothers", "Adolescents (WIFS)"],
                                        datasets: [
                                            {
                                                label: "Coverage (%)",
                                                data: [76, 69, 63],
                                                backgroundColor: ["#2563EB", "#7C3AED", "#0EA5E9"],
                                                borderRadius: 6,
                                            },
                                        ],
                                    }}
                                    options={compactChartOptions}
                                />
                            </div>
                        </div>

                        {/* COMPLIANCE DONUT */}
                        <div className="bg-white rounded-xl shadow-sm p-3">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                IFA Consumption Compliance
                            </h3>

                            {/* ✅ FIXED HEIGHT */}
                            <div className="relative w-full h-[240px]">
                                <Doughnut
                                    data={{
                                        labels: ["Compliant", "Partially Compliant", "Non-compliant"],
                                        datasets: [
                                            {
                                                data: [58, 24, 18],
                                                backgroundColor: ["#16A34A", "#F59E0B", "#EF4444"],
                                            },
                                        ],
                                    }}
                                    options={donutOptions}
                                />
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-1">
                                Nearly 2 out of 5 beneficiaries do not consume IFA adequately
                            </p>
                        </div>

                    </div>

                    {/* INSIGHT STRIP */}

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-xl mt-3">
                        <p className="text-sm text-blue-800 leading-relaxed">
                            <b>Key Insight:</b> Despite good IFA distribution coverage, compliance remains a key gap—especially among adolescents. Strengthening counselling and follow-up can significantly improve anemia outcomes.
                        </p>
                    </div>
                </section >

                {/* ================= SECTION: OUTCOME & IMPACT ================= */}
                < section className="space-y-4" >
                    <h2 className="text-lg font-semibold text-gray-800">
                        Outcome & Impact 
                    </h2>

                    {/* KPI OUTCOMES */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard title="Reduction in Anemia (YoY)" value="-5%" color="#16A34A" />
                        <KpiCard title="Women with Hb ≥11 at Registration" value="56%" color="#2563EB" />
                        <KpiCard title="Hb <7 in 3rd Trimester" value="4%" color="#DC2626" />
                        <KpiCard title="Children <5 Improved Hb" value="+7%" color="#10B981" />
                    </div>

                    {/* VISUAL TRENDS */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                        {/* PREVALENCE TREND */}
                        <div className="bg-white rounded-xl shadow-sm p-3 lg:col-span-2 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                Anemia Prevalence Trend (Year-wise)
                            </h3>

                            {/* ✅ FIXED HEIGHT */}
                            <div className="relative w-full h-[260px]">
                                <Bar
                                    data={{
                                        labels: ["2021", "2022", "2023", "2024", "2025"],
                                        datasets: [
                                            {
                                                label: "Overall Anemia %",
                                                data: [54, 52, 49, 46, 44],
                                                backgroundColor: "#EF4444",
                                                borderRadius: 6,
                                            },
                                        ],
                                    }}
                                    options={compactChartOptions}
                                />
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-1">
                                Consistent year-on-year reduction, but pace needs acceleration
                            </p>
                        </div>

                        {/* CHILDREN OUTCOME DONUT */}
                        <div className="bg-white rounded-xl shadow-sm p-3 flex flex-col">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                Children Under 5 – Anemia Status
                            </h3>

                            {/* ✅ FIXED HEIGHT */}
                            <div className="relative w-full h-[220px] flex items-center justify-center">
                                <Doughnut
                                    data={{
                                        labels: ["Anemic", "Normal"],
                                        datasets: [
                                            {
                                                data: [41, 59],
                                                backgroundColor: ["#F59E0B", "#16A34A"],
                                            },
                                        ],
                                    }}
                                    options={donutOptions}
                                />
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-1">
                                4 out of 10 children remain anemic
                            </p>
                        </div>
                    </div>
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-xl mt-3">
                        <p className="text-sm text-amber-800 leading-relaxed">
                            <b>Key Insight:</b> Despite a steady decline in anemia prevalence,
                            severe anemia persists among pregnant women and children under 5.
                            Program impact will improve with early registration, strict follow-up,
                            and higher IFA compliance.
                        </p>
                    </div>
                </section >

                {/* ================= SECTION: EQUITY & GEOGRAPHIC INSIGHTS ================= */}
                < section className="space-y-4" >
                    <h2 className="text-lg font-semibold text-gray-800">
                        Equity & Geographic Insights 
                    </h2>

                    {/* KPI SNAPSHOT */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <KpiCard title="High Burden Districts" value="12" color="#DC2626" />
                        <KpiCard title="High Burden Blocks" value="47" color="#EF4444" />
                        <KpiCard title="Urban Anemia Prevalence" value="38%" color="#3B82F6" />
                        <KpiCard title="Rural Anemia Prevalence" value="49%" color="#F97316" />
                    </div>

                    {/* VISUAL ROW */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                        {/* HEATMAP PLACEHOLDER */}
                        <div className="bg-white rounded-xl shadow-sm p-3 lg:col-span-2">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                District-wise Anemia Burden (Heatmap)
                            </h3>
                            <div className="relative h-[520px] border border-dashed rounded-lg">
                                {stateGeoJSON ? (
                                    <>
                                        {/* MAP CONTAINER */}
                                        <div className="h-full w-full">
                                            <MapContainer
                                                center={[19.5, 75.5]}
                                                minZoom={7}
                                                maxZoom={7}
                                                zoom={6}
                                                style={{ height: "100%", width: "100%" }}
                                                scrollWheelZoom={true}
                                                zoomControl={true} // disable default zoom buttons
                                            >
                                                <TileLayer
                                                    attribution=""
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />
                                                <GeoJSON
                                                    data={stateGeoJSON}
                                                    style={(feature) => {
                                                        const districtName = feature.properties?.district || feature.properties?.DISTRICT;
                                                        const value = districtAnemia[districtName] || 0;
                                                        return {
                                                            fillColor: getColor(value),
                                                            weight: 1,
                                                            color: "#334155",
                                                            fillOpacity: 0.7,
                                                        };
                                                    }}
                                                    onEachFeature={(feature, layer) => {
                                                        const districtName = feature.properties?.district || feature.properties?.DISTRICT;
                                                        const value = districtAnemia[districtName] || "NA";
                                                        layer.bindTooltip(
                                                            `<b>${districtName}</b><br/>Anemia Prevalence: ${value}%`,
                                                            { sticky: true }
                                                        );
                                                    }}
                                                />
                                            </MapContainer>
                                        </div>

                                        {/* LEGEND - OVER MAP */}
                                        <div
                                            className="absolute bottom-2 right-2 bg-white bg-opacity-90 p-2 rounded shadow text-xs text-gray-600 pointer-events-none"
                                            style={{ zIndex: 1000 }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 bg-[#7F1D1D] inline-block"></span> ≥55% (Very High)
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 bg-[#DC2626] inline-block"></span> 50–54%
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 bg-[#F97316] inline-block"></span> 45–49%
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 bg-[#FACC15] inline-block"></span> 40–44%
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-3 h-3 bg-[#16A34A] inline-block"></span> &lt;40%
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        Loading map...
                                    </div>
                                )}
                            </div>




                            <p className="text-xs text-gray-500 mt-1">
                                Darker districts indicate higher moderate + severe anemia prevalence
                            </p>
                        </div>

                        {/* TOP 10 DISTRICTS */}
                        <div className="bg-white rounded-xl shadow-sm p-3">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                Top 10 Highest Burden Districts
                            </h3>

                            <div className="aspect-square">
                                <Bar data={topDistrictsData} options={topDistrictsOptions} />
                            </div>

                            <p className="text-xs text-gray-500 mt-2">
                                Darker red indicates higher anemia burden
                            </p>
                        </div>

                    </div>

                    {/* INSIGHT STRIP */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                        <p className="text-sm text-gray-700">
                            <b>Equity Insight:</b> Rural anemia prevalence is <b>11 percentage points higher</b> than urban areas.
                            Concentration of high-burden districts in tribal and aspirational regions indicates the need for
                            <b> block-focused micro-plans, mobile screening units, and intensified supplementation drives</b>.
                        </p>
                    </div>
                </section >

            </div >
        </div>
    );
}
